import { Button, Heading, Paragraph, PasswordForm, Tabs, useToast } from "@aidkitorg/component-library";
import { browserSupportsWebAuthn, startRegistration } from "@simplewebauthn/browser";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { usePost } from "../API";
import { ThreeColumnPage } from "../Components/ThreeColumnPage";
import InterfaceContext, { PublicConfigurationContext } from "../Context";
import { useLocalizedStrings } from "../Localization";
import { generatePasskeyLabel, passkeyBackendErrorKind, passkeysEnabled } from "../passkeys";
import { SpacedSpinner } from "../Util";

export const AdminAccountSettings = () => {
  const { toast } = useToast();
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const config = useContext(PublicConfigurationContext);
  const history = useHistory();
  const passkeysAreEnabled = passkeysEnabled(config);
  const defaultPasskeyLabel = useMemo(() => generatePasskeyLabel({
    locale: context.lang,
    environment: navigator,
  }), [context.lang]);

  const [changingPassword, setChangingPassword] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState<string>("");
  const [passkeys, setPasskeys] = useState<Array<{
    id: string;
    label: string;
    created_at: string;
    last_used_at: string | null;
  }>>([]);
  const [passkeyLabels, setPasskeyLabels] = useState<Record<string, string>>({});
  const [newPasskeyLabel, setNewPasskeyLabel] = useState(defaultPasskeyLabel);
  const [loadingPasskeys, setLoadingPasskeys] = useState(true);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  
  const resetAdminPassword = usePost("/authenticate/password/admin/reset");
  const getPasskeys = usePost('/authenticate/passkey/list');
  const getRegistrationOptions = usePost('/authenticate/passkey/registration/options', { suppressErrorDisplay: true });
  const completeRegistration = usePost('/authenticate/passkey/registration/complete', { suppressErrorDisplay: true });
  const renameStoredPasskey = usePost('/authenticate/passkey/rename', { suppressErrorDisplay: true });
  const deleteStoredPasskey = usePost('/authenticate/passkey/delete', { suppressErrorDisplay: true });

  const redirectForRecentAuth = (response: { error?: string } | undefined) => {
    if (response?.error !== 'recent_auth_required') return false;
    history.push(`/login?next=${encodeURIComponent('/admin/account_settings')}`);
    return true;
  };

  const toastPasskeyApiError = (response: { error?: string } | undefined) => {
    if (!response?.error || response.error === 'recent_auth_required') return;
    const kind = passkeyBackendErrorKind(response.error);
    toast({
      title: kind === 'expired' ? L.passkeys.expired_title
        : kind === 'disabled' ? L.passkeys.disabled_title
          : L.passkeys.failed_title,
      description: kind === 'expired' ? L.passkeys.expired_description
        : kind === 'disabled' ? L.passkeys.disabled_description
          : L.passkeys.failed_description,
      variant: 'error',
    });
  };

  const refreshPasskeys = async () => {
    if (!passkeysAreEnabled) return;
    setLoadingPasskeys(true);
    try {
      const response = await getPasskeys({});
      if ('passkeys' in response && response.passkeys) {
        setPasskeys(response.passkeys as typeof passkeys);
        setPasskeyLabels(Object.fromEntries(response.passkeys.map(passkey => [passkey.id, passkey.label])));
      }
    } finally {
      setLoadingPasskeys(false);
    }
  };

  useEffect(() => {
    void refreshPasskeys();
  }, [passkeysAreEnabled]);

  useEffect(() => {
    // Public configuration starts as an empty object. Wait for both feature flags
    // to be populated before deciding this page is unavailable, and navigate only
    // after render so the router is not updated during a React state transition.
    if (config.adminPasswords === false && !passkeysAreEnabled) {
      history.push("/");
    }
  }, [config.adminPasswords, history, passkeysAreEnabled]);

  const addPasskey = async () => {
    setPasskeyBusy(true);
    try {
      const options = await getRegistrationOptions({});
      if (redirectForRecentAuth(options)) return;
      if ('error' in options && options.error) {
        toastPasskeyApiError(options);
        return;
      }
      if (!('options' in options) || !options.options || !options.ceremony_id) {
        throw new Error('Missing passkey registration options');
      }
      const response = await startRegistration({ optionsJSON: options.options });
      const completion = await completeRegistration({
        ceremony_id: options.ceremony_id,
        label: newPasskeyLabel,
        response,
      });
      if (redirectForRecentAuth(completion)) return;
      if ('verified' in completion && completion.verified) {
        toast({ title: L.passkeys.added_title, description: L.passkeys.added_description, variant: 'success' });
        setNewPasskeyLabel(defaultPasskeyLabel);
        await refreshPasskeys();
        return;
      }
      toastPasskeyApiError(completion);
    } catch (error: any) {
      if (error?.name !== 'NotAllowedError' && error?.code !== 'ERROR_CEREMONY_ABORTED') {
        toast({ description: L.passkeys.failed_description, variant: 'error' });
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const renamePasskey = async (credentialId: string) => {
    setPasskeyBusy(true);
    try {
      const response = await renameStoredPasskey({
        credential_id: credentialId,
        label: passkeyLabels[credentialId],
      });
      if (redirectForRecentAuth(response)) return;
      if ('ok' in response && response.ok) {
        toast({ description: L.passkeys.renamed, variant: 'success' });
        await refreshPasskeys();
        return;
      }
      toastPasskeyApiError(response);
    } finally {
      setPasskeyBusy(false);
    }
  };

  const removePasskey = async (credentialId: string) => {
    if (!window.confirm(L.passkeys.delete_confirmation)) return;
    setPasskeyBusy(true);
    try {
      const response = await deleteStoredPasskey({ credential_id: credentialId });
      if (redirectForRecentAuth(response)) return;
      if ('ok' in response && response.ok) {
        toast({ description: L.passkeys.deleted, variant: 'success' });
        await refreshPasskeys();
        return;
      }
      if ('error' in response && response.error === 'last_passkey_required') {
        toast({ description: L.passkeys.last_passkey_required, variant: 'error' });
      } else {
        toastPasskeyApiError(response);
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const uppercaseRegex = /[A-Z]/;
  const lowercaseRegex = /[a-z]/;
  const numberRegex = /\d/;
  const specialCharRegex = /[!@#$%^&*(),.?":{}|<>~`_\+\-=\[\]\\;]/;
  const specialCharLabel = (
    <div className="flex flex-col">
      <span>At least one special character</span>
      <span>{'[!@#$%^&*(),.?":{}|<>~`_\+\-=\[\]\\;]'}</span>
    </div>
  );

  const submitResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setChangingPassword(true);
    
    try {
      const formData = new FormData(e.currentTarget);
      const values = Object.fromEntries(formData.entries());
      
      const response = await resetAdminPassword({
        password: values.password.toString(),
        oldPassword: values.current_password.toString()
      });

      if (response && response.status === 'ok') {
        toast({
          title: "Update Successful",
          description: "Your password has been successfully updated.",
          variant: "success",
        })

        // Reset all the values
        setCurrentPassword('');
        setNewPassword('');
        setNewPasswordConfirm('');
      } else {
        // API errors will be handled by our main frontend API handler with toasts
        return;
      }
    } catch (e) {
      console.error(e);
      // This will only catch front-end errors
      toast({description: "Error changing password", variant: "error"})
    } finally {
      setChangingPassword(false);
    }
  }  

  const PasswordSettings = config.adminPasswords ? (
    <div className="rounded border p-4 max-w-2xl">
      <span className="font-bold inline-block mb-2">{L.admin.account_settings.change_password}</span>
      <PasswordForm
        verifyCurrentPassword
        loading={changingPassword}
        value={newPassword}
        onValueChange={(e) => setNewPassword(e.target.value)}
        confirmationValue={newPasswordConfirm}
        onConfirmationValueChange={(e) => setNewPasswordConfirm(e.target.value)}
        currentPassword={currentPassword}
        onCurrentPasswordChange={(e) => setCurrentPassword(e.target.value)}
        onSubmit={submitResetPassword}
        validationRules={
          [
            { regex: /.{16,}/, label: "At least 16 characters" },
            { regex: /^.{1,63}$/, label: "Less than 64 characters" },
            { regex: uppercaseRegex, label: "At least one uppercase character" },
            { regex: lowercaseRegex, label: "At least one lowercase character" },
            { regex: numberRegex, label: "At least one number" },
            { regex: specialCharRegex, label: specialCharLabel },
          ]
        }
      />
    </div>
  ) : null;

  const PasskeySettings = passkeysAreEnabled ? (
    <div className="rounded border p-4 max-w-2xl space-y-4">
      <div>
        <span className="font-bold inline-block mb-2">{L.passkeys.manage_title}</span>
        <Paragraph className="mt-0 text-gray-700">{L.passkeys.manage_description}</Paragraph>
      </div>
      {passkeys.length === 0 && !loadingPasskeys && (
        <Paragraph>{L.passkeys.no_passkeys}</Paragraph>
      )}
      {passkeys.map(passkey => (
        <div key={passkey.id} className="rounded border p-3 space-y-2">
          <label htmlFor={`passkey-${passkey.id}`} className="block text-sm font-medium">{L.passkeys.label}</label>
          <input
            id={`passkey-${passkey.id}`}
            value={passkeyLabels[passkey.id] ?? passkey.label}
            maxLength={100}
            onChange={event => setPasskeyLabels(labels => ({ ...labels, [passkey.id]: event.target.value }))}
            className="block w-full rounded-md border-0 py-2 px-2 text-gray-900 ring-1 ring-inset ring-gray-300"
          />
          <Paragraph size="sm" className="text-gray-600 m-0">
            {L.passkeys.created.replace('$date', new Date(passkey.created_at).toLocaleDateString())}
            {passkey.last_used_at ? ` · ${L.passkeys.last_used.replace('$date', new Date(passkey.last_used_at).toLocaleDateString())}` : ''}
          </Paragraph>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" isDisabled={passkeyBusy || loadingPasskeys} onClick={() => renamePasskey(passkey.id)}>{L.passkeys.rename}</Button>
            <Button type="button" variant="danger" isDisabled={passkeyBusy || loadingPasskeys} onClick={() => removePasskey(passkey.id)}>{L.passkeys.delete}</Button>
          </div>
        </div>
      ))}
      {browserSupportsWebAuthn() && (
        <div className="border-t pt-4 space-y-2">
          <label htmlFor="new-passkey-label" className="block text-sm font-medium">{L.passkeys.new_label}</label>
          <input
            id="new-passkey-label"
            value={newPasskeyLabel}
            maxLength={100}
            onChange={event => setNewPasskeyLabel(event.target.value)}
            className="block w-full rounded-md border-0 py-2 px-2 text-gray-900 ring-1 ring-inset ring-gray-300"
          />
          <Button type="button" variant="primary" loading={passkeyBusy} isDisabled={passkeyBusy || loadingPasskeys} onClick={addPasskey}>{L.passkeys.add_passkey}</Button>
        </div>
      )}
      <Paragraph size="sm" className="text-gray-600">
        {config.mandatoryPasskeys ? L.passkeys.recovery_admin_only : L.passkeys.recovery_available}
      </Paragraph>
    </div>
  ) : null;

  const SecurityTab = (
    <div className="my-4 space-y-4">
      {PasswordSettings}
      {PasskeySettings}
    </div>
  );

  const Loading = (
    <div className="flex justify-center items-center h-screen">
      <div className="sr-only">{L.applicant.loading}</div>
      <SpacedSpinner />
    </div>
  );

  const MainContent = (
    <div className="bg-gray-50 h-full">
      <div className="max-w-5xl p-8 mx-auto">
        <Heading level="h2">{L.admin.account_settings.title}</Heading>
        <Tabs
          justify="left"
          tabs={
            [
              {
                name: L.admin.account_settings.security,
                key: 'security',
                content: SecurityTab
              }
            ]
          }
        />
      </div>
    </div>
  );
  
  return (
    <ThreeColumnPage 
      main={
        config && (config.adminPasswords || passkeysAreEnabled) ? MainContent : Loading
      }
    /> 
  );
};
